import React, { useCallback, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DatePicker from "../../../Components/DatePicker";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import dayjs from "dayjs";
import { clientApis, clientDriversApis, driverLeaveApis } from "../../../Data/axios";
import config from "../../../Data/AuthUser";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputSx = {
  "& .MuiOutlinedInput-root fieldset": { borderColor: "#b0b0b0" },
  "& .MuiOutlinedInput-input": { color: "#000" },
  "& .MuiInputLabel-root": { color: "#666" },
  "& .MuiSvgIcon-root": { color: "#000" },
};

const dateSlotProps = { textField: { placeholder: "", sx: inputSx } };

const fmt = (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "");

const AddLeaveDialog = ({ open, onClose, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [client, setClient] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driver, setDriver] = useState(null);
  const [duration, setDuration] = useState("1");
  const [oneDate, setOneDate] = useState(null);
  const [range, setRange] = useState([null, null]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setClient(null);
    setDriver(null);
    setDrivers([]);
    setDuration("1");
    setOneDate(null);
    setRange([null, null]);
    setReason("");
  };

  const fetchClients = useCallback(async (query) => {
    setLoadingClients(true);
    try {
      const res = await clientApis.getAll(1, 50, "company_name", query || "");
      const list = res?.data?.data || res?.data || [];
      setClients(Array.isArray(list) ? list : []);
    } catch {
      setClients([]);
    }
    setLoadingClients(false);
  }, []);

  // only status = 1 (active) drivers of the picked client
  const fetchDrivers = useCallback(async (clientUserId) => {
    setDrivers([]);
    setDriver(null);
    if (!clientUserId) return;
    setLoadingDrivers(true);
    try {
      const res = await clientDriversApis.getClientDriversList(clientUserId);
      const list = res?.data?.active_drivers || res?.active_drivers || [];
      setDrivers(Array.isArray(list) ? list.filter((d) => d && (d.status === undefined || String(d.status) === "1")) : []);
    } catch {
      setDrivers([]);
    }
    setLoadingDrivers(false);
  }, []);

  const startDate = duration === "1" ? oneDate : range[0];
  const endDate = duration === "1" ? oneDate : range[1];

  const handleSave = async () => {
    if (!client) return alert("Please select a client");
    if (!driver) return alert("Please select a driver");
    if (!startDate || !endDate) return alert("Please select the leave date");
    if (!reason.trim()) return alert("Please enter a reason");

    setSaving(true);
    const formData = new FormData();
    formData.append("owner_id", client.user_id || client.id);
    formData.append("driver_id", driver.user_id);
    formData.append("type", duration);
    formData.append("start_date", dayjs(startDate).format("YYYY-MM-DD"));
    formData.append("end_date", dayjs(endDate).format("YYYY-MM-DD"));
    formData.append("reason", reason.trim());

    try {
      await driverLeaveApis.add(formData);
      reset();
      onClose();
      onSaved?.();
    } catch {
      alert("Failed to save leave request");
    }
    setSaving(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{ style: { backgroundColor: "#fff", color: "#000" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #e0e0e0", color: "#000" }}>
          Add Leave Request
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={clients}
                getOptionLabel={(o) => o.company_name || ""}
                isOptionEqualToValue={(o, v) =>
                  o.id === v?.id || o.user_id === v?.user_id
                }
                value={client}
                loading={loadingClients}
                componentsProps={{ paper: { sx: { bgcolor: "#fff", color: "#000" } } }}
                onOpen={() => clients.length === 0 && fetchClients("")}
                onInputChange={(_, val) => fetchClients(val)}
                onChange={(_, val) => {
                  setClient(val);
                  fetchDrivers(val?.user_id);
                }}
                renderOption={(props, option) => {
                  const { key, ...other } = props;
                  return (
                    <li key={key} {...other}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body1" sx={{ color: "#000" }}>
                          {option.company_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666" }}>
                          {option.user_id}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    fullWidth
                    sx={inputSx}
                    InputProps={{
                      ...params.InputProps,
                      style: { color: "#000" },
                      endAdornment: (
                        <>
                          {loadingClients ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={drivers}
                getOptionLabel={(o) => o.username || ""}
                isOptionEqualToValue={(o, v) => o.user_id === v?.user_id}
                value={driver}
                loading={loadingDrivers}
                disabled={!client}
                componentsProps={{ paper: { sx: { bgcolor: "#fff", color: "#000" } } }}
                onChange={(_, val) => setDriver(val)}
                renderOption={(props, option) => {
                  const { key, ...other } = props;
                  return (
                    <li key={key} {...other}>
                      <Box display="flex" alignItems="center">
                        <img
                          src={
                            option.image && option.image !== "non"
                              ? `${config.apiImageUrl}${option.image}`
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  option.username || "D"
                                )}&background=random`
                          }
                          alt="driver"
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            marginRight: 12,
                            objectFit: "cover",
                          }}
                        />
                        <Box>
                          <Typography variant="body1" sx={{ color: "#000" }}>
                            {option.username}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {option.user_id}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver"
                    fullWidth
                    sx={inputSx}
                    InputProps={{
                      ...params.InputProps,
                      style: { color: "#000" },
                      endAdornment: (
                        <>
                          {loadingDrivers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel sx={{ color: "#000", mb: 0.5 }}>Duration</FormLabel>
              <RadioGroup
                row
                value={duration}
                onChange={(e) => {
                  setDuration(e.target.value);
                  setOneDate(null);
                  setRange([null, null]);
                }}
              >
                <FormControlLabel
                  value="1"
                  control={<Radio sx={{ color: "#000" }} />}
                  label="One Day"
                  sx={{ color: "#000", mr: 3 }}
                />
                <FormControlLabel
                  value="2"
                  control={<Radio sx={{ color: "#000" }} />}
                  label="Multiple Days"
                  sx={{ color: "#000" }}
                />
              </RadioGroup>
            </Grid>

            <Grid item xs={12}>
              {duration === "1" ? (
                <DatePicker
                  label="Leave Date"
                  value={oneDate}
                  onChange={setOneDate}
                  format="DD/MM/YYYY"
                  slotProps={dateSlotProps}
                  sx={inputSx}
                />
              ) : (
                <DateRangePicker
                  value={range}
                  onChange={setRange}
                  format="DD/MM/YYYY"
                  slotProps={dateSlotProps}
                />
              )}
            </Grid>

            {startDate && endDate ? (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#000" }}>
                  {duration === "1"
                    ? fmt(startDate)
                    : `${fmt(startDate)} to ${fmt(endDate)}`}
                </Typography>
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <TextField
                label="Reason"
                fullWidth
                multiline
                minRows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
          <Button onClick={onClose} sx={{ color: "#000" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddLeaveDialog;
